import type { CompanyApplication } from "../../types/shared";
import { AppError } from "../../../shared/utils/error-handler";
import type { CompanyRepository } from "../../repositories/company/company-repository";
import type {
  GetCompanyInfoRequest,
  RealtimeGraphVertex,
  SearchCompanyUsersRequest,
  SearchCompanyUsersResponse,
  UpdateCompanyInfoRequest,
  UpdateUserRoleRequest,
  WithdrawCompanyUserRequest,
} from "../../types/company";

import type { CompanyInfoResponse, UserInfoResponse } from "../../types/shared";

export class CompanyUseCase {
  constructor(private companyRepository: CompanyRepository) {}

  async getCompanyInfo(
    request: GetCompanyInfoRequest,
  ): Promise<CompanyInfoResponse> {
    try {
      return await this.companyRepository.getCompanyInfo(request);
    } catch (error) {
      throw new AppError(
        String(error) || "업체 정보를 불러오지 못했습니다.",
        "COMPANY_INFO_GET_FAILED",
        error,
      );
    }
  }

  async updateCompanyInfo(
    request: UpdateCompanyInfoRequest,
  ): Promise<CompanyInfoResponse> {
    try {
      return await this.companyRepository.updateCompanyInfo(request);
    } catch (error) {
      throw new AppError(
        String(error) || "업체 정보를 수정하지 못했습니다.",
        "COMPANY_INFO_UPDATE_FAILED",
        error,
      );
    }
  }

  async updateUserRole(request: UpdateUserRoleRequest): Promise<boolean> {
    try {
      return await this.companyRepository.updateUserRole(request);
    } catch (error) {
      throw new AppError(
        String(error) || "유저 역할을 수정하지 못했습니다.",
        "USER_ROLE_UPDATE_FAILED",
        error,
      );
    }
  }

  /** 회사 소속 쇼핑몰 회원 탈퇴 처리 (되돌릴 수 없으며, 대상 이메일로 안내 메일이 발송된다) */
  async withdrawCompanyUser(
    request: WithdrawCompanyUserRequest,
  ): Promise<boolean> {
    try {
      return await this.companyRepository.withdrawCompanyUser(request);
    } catch (error) {
      throw new AppError(
        String(error) || "회원을 탈퇴 처리하지 못했습니다.",
        "COMPANY_USER_WITHDRAW_FAILED",
        error,
      );
    }
  }

  async requestCompany(application: CompanyApplication): Promise<UserInfoResponse> {
    try {
      return await this.companyRepository.requestCompany(application);
    } catch (error) {
      throw new AppError(
        String(error) || "업체 신청에 실패하였습니다.",
        "COMPANY_REQUEST_FAILED",
        error,
      );
    }
  }

  async getUserByCompanyId(
    request: SearchCompanyUsersRequest,
  ): Promise<SearchCompanyUsersResponse> {
    try {
      return await this.companyRepository.getUserByCompanyId(request);
    } catch (error) {
      throw new AppError(
        String(error) || "업체 유저 목록을 불러오지 못했습니다.",
        "COMPANY_USER_SEARCH_FAILED",
        error,
      );
    }
  }

  async getCompanyRealtimeGraph(
    companyId: number,
  ): Promise<RealtimeGraphVertex[]> {
    try {
      return await this.companyRepository.getCompanyRealtimeGraph(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "업체 실시간 그래프 데이터를 불러오지 못했습니다.",
        "REALTIME_GRAPH_GET_FAILED",
        error,
      );
    }
  }

  async getCompanyAdminInfo(companyId: number): Promise<UserInfoResponse> {
    try {
      return await this.companyRepository.getCompanyAdminInfo(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "업체 어드민 정보를 불러오지 못했습니다.",
        "COMPANY_ADMIN_INFO_GET_FAILED",
        error,
      );
    }
  }

  async getEmailSettings(
    companyId: number,
  ): Promise<{ data: { senderEmail: string | null; hasPassword: boolean } }> {
    try {
      return await this.companyRepository.getEmailSettings(companyId);
    } catch (error) {
      throw new AppError(
        String(error) || "이메일 설정을 불러오지 못했습니다.",
        "EMAIL_SETTINGS_GET_FAILED",
        error,
      );
    }
  }

  async updateEmailSettings(
    companyId: number,
    senderEmail: string,
    senderEmailPassword: string,
  ): Promise<{ data: { senderEmail: string | null; hasPassword: boolean } }> {
    try {
      return await this.companyRepository.updateEmailSettings(
        companyId,
        senderEmail,
        senderEmailPassword,
      );
    } catch (error) {
      throw new AppError(
        String(error) || "이메일 설정 수정에 실패했습니다.",
        "EMAIL_SETTINGS_UPDATE_FAILED",
        error,
      );
    }
  }

  async updateTossSecretKey(request: {
    companyId: number;
    tossSecretKey: string;
  }): Promise<boolean> {
    try {
      return await this.companyRepository.updateTossSecretKey(request);
    } catch (error) {
      throw new AppError(
        String(error) || "토스페이먼츠 Secret Key 설정에 실패했습니다.",
        "TOSS_SECRET_KEY_UPDATE_FAILED",
        error,
      );
    }
  }
}
